<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import { useDisplayName } from '@/composables/useDisplayName';
import UserAvatar from '@/core/UserAvatar.vue';
import ThreeDotsActionDialog from '@/core/dialogs/ThreeDotsActionDialog.vue';
import KeyboardNavigableList from '@/core/elements/KeyboardNavigableList.vue';
import StudentListAddAction from '@/core/students/StudentListAddAction.vue';
import StudentListOverviewItem from '@/core/students/StudentListOverviewItem.vue';
import type { IDropdownListAction } from '@/interfaces';
import { EIcon } from 'common/enum/misc.enum';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const selectedStudentId = defineModel<number | null>('selectedStudentId', { default: null });

const props = defineProps<{
  overviewLabel: string;
  students: Components.Schemas.SupportMeasureFilterStudentDto[];
  readonly?: boolean;
  showAddStudents?: boolean;
}>();

const emit = defineEmits<{
  readd: [studentId: number];
  'remove-finally': [studentId: number];
  'request-add': [anchor: HTMLElement];
}>();

const { t } = useI18n();
const { getStudentDisplayName } = useDisplayName();

const activeStudents = computed(() => props.students.filter((student) => !student.isRemoved));
const inactiveStudents = computed(() => props.students.filter((student) => student.isRemoved));

const showInactive = ref(false);

interface IStudentFilterItem {
  key: number | 'all';
  student: Components.Schemas.SupportMeasureFilterStudentDto | null;
}

const studentFilterItems = computed<IStudentFilterItem[]>(() => [
  { key: 'all', student: null },
  ...activeStudents.value.map((student) => ({ key: student.id, student })),
  ...(showInactive.value ? inactiveStudents.value.map((student) => ({ key: student.id, student })) : []),
]);
const getStudentFilterKey = (item: IStudentFilterItem): number | string => item.key;
const getStudentFilterValue = (item: IStudentFilterItem): number | null => item.student?.id ?? null;

const rowHighlightClass = (id: number | null): string =>
  selectedStudentId.value === id ? 'bg-surface-tertiary' : 'hover:bg-surface-secondary';
const rowTextClass = (id: number | null, removed = false): string => {
  if (selectedStudentId.value === id) return 'text-tint font-semibold';
  return removed ? 'text-gray' : 'text-primary';
};

const inactiveActions = (studentId: number): IDropdownListAction[] => [
  {
    label: t('supportMeasureEntry.readdStudent'),
    iconBefore: EIcon.ADD_USER,
    separatorAfter: true,
    action: () => emit('readd', studentId),
  },
  {
    label: t('supportMeasureEntry.removeAndDeleteEntries'),
    iconBefore: EIcon.USERREMOVE,
    style: 'error',
    action: () => emit('remove-finally', studentId),
  },
];
</script>

<template>
  <KeyboardNavigableList
    class="flex flex-col gap-xs"
    :get-key="getStudentFilterKey"
    :get-value="getStudentFilterValue"
    :items="studentFilterItems"
    :model-value="selectedStudentId"
    @update:model-value="selectedStudentId = $event"
  >
    <template #default="{ item, itemAttributes, select, selected }">
      <StudentListOverviewItem
        v-if="!item.student"
        v-bind="itemAttributes"
        :label="overviewLabel"
        :selected="selected"
        @select="select"
      />

      <button
        v-else-if="!item.student.isRemoved"
        v-bind="itemAttributes"
        type="button"
        class="flex w-full items-center gap-md rounded-md px-sm py-sm text-left transition-colors"
        :class="[rowHighlightClass(item.student.id), rowTextClass(item.student.id)]"
        :aria-pressed="selected"
        @click="select"
      >
        <UserAvatar :user="item.student" class="size-icon-lg shrink-0" />
        <span class="min-w-0 truncate">{{ getStudentDisplayName(item.student) }}</span>
      </button>

      <div
        v-else
        class="flex items-center rounded-md pr-sm transition-colors"
        :class="rowHighlightClass(item.student.id)"
      >
        <button
          v-bind="itemAttributes"
          type="button"
          class="flex min-w-0 flex-1 items-center gap-md px-sm py-sm text-left"
          :class="rowTextClass(item.student.id, true)"
          :aria-pressed="selected"
          @click="select"
        >
          <UserAvatar :user="item.student" class="size-icon-lg shrink-0" />
          <span class="min-w-0 truncate">{{ getStudentDisplayName(item.student) }}</span>
        </button>
        <ThreeDotsActionDialog
          v-if="!readonly"
          :actions="inactiveActions(item.student.id)"
          :min-width="280"
          size="sm"
        />
      </div>
    </template>

    <template #footer>
      <StudentListAddAction v-if="showAddStudents" @request="emit('request-add', $event)" />
      <template v-if="inactiveStudents.length">
        <li v-if="showInactive">
          <button
            type="button"
            class="px-sm py-sm text-left text-body text-secondary underline underline-offset-2"
            @click="showInactive = false"
          >
            {{ $t('supportMeasureEntry.inactiveStudentsHide') }}
          </button>
        </li>
        <li v-else>
          <button
            type="button"
            class="px-sm py-sm text-left text-body text-secondary underline underline-offset-2"
            @click="showInactive = true"
          >
            {{ $t('supportMeasureEntry.inactiveStudentsShow', { count: inactiveStudents.length }) }}
          </button>
        </li>
      </template>
    </template>
  </KeyboardNavigableList>
</template>
